import { z } from 'zod';
import { ulidSchema } from './id';

export const CARD_AGING_MODES = ['off', 'regular', 'pirate'] as const;

export const cardAgingModeSchema = z.enum(CARD_AGING_MODES);

export type CardAgingMode = z.infer<typeof cardAgingModeSchema>;

export const CARD_AGING_STAGES = ['fresh', 'week_1', 'week_2', 'week_4'] as const;

export const cardAgingStageSchema = z.enum(CARD_AGING_STAGES);

export type CardAgingStage = z.infer<typeof cardAgingStageSchema>;

export const CARD_AGING_STAGE_THRESHOLDS_DAYS = {
  fresh: 0,
  week_1: 7,
  week_2: 14,
  week_4: 28,
} as const satisfies Record<CardAgingStage, number>;

export const boardCardAgingModeSchema = z.object({
  boardId: ulidSchema,
  cardAgingMode: cardAgingModeSchema,
});

export type BoardCardAgingMode = z.infer<typeof boardCardAgingModeSchema>;

export const boardCardAgingModePatchSchema = z.strictObject({
  cardAgingMode: cardAgingModeSchema,
});

export type BoardCardAgingModePatch = z.infer<typeof boardCardAgingModePatchSchema>;

export const cardAgingExemptPatchSchema = z.strictObject({
  agingExempt: z.boolean(),
});

export type CardAgingExemptPatch = z.infer<typeof cardAgingExemptPatchSchema>;

export const cardEvergreenPatchSchema = cardAgingExemptPatchSchema;

export type CardEvergreenPatch = CardAgingExemptPatch;

export const cardAgingFieldsSchema = z.object({
  agingExempt: z.boolean(),
  lastActivityAt: z.string().min(1),
  agingStage: cardAgingStageSchema.nullable(),
});

export type CardAgingFields = z.infer<typeof cardAgingFieldsSchema>;

export interface ComputeCardAgingStageInput {
  lastActivityAt: string | number | Date;
  now: string | number | Date;
  mode: CardAgingMode;
  exempt: boolean;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function toTime(value: string | number | Date, field: 'lastActivityAt' | 'now'): number {
  const time = value instanceof Date ? value.getTime() : new Date(value).getTime();

  if (!Number.isFinite(time)) {
    throw new RangeError(`${field} must be a valid date-like value`);
  }

  return time;
}

export function computeCardAgingStage({
  lastActivityAt,
  now,
  mode,
  exempt,
}: ComputeCardAgingStageInput): CardAgingStage | null {
  if (mode === 'off' || exempt) {
    return null;
  }

  const elapsedDays = Math.max(
    0,
    (toTime(now, 'now') - toTime(lastActivityAt, 'lastActivityAt')) / MS_PER_DAY,
  );

  if (elapsedDays >= CARD_AGING_STAGE_THRESHOLDS_DAYS.week_4) {
    return 'week_4';
  }

  if (elapsedDays >= CARD_AGING_STAGE_THRESHOLDS_DAYS.week_2) {
    return 'week_2';
  }

  if (elapsedDays >= CARD_AGING_STAGE_THRESHOLDS_DAYS.week_1) {
    return 'week_1';
  }

  return 'fresh';
}
